import { css } from 'twin.macro';
import React from 'react';
import { Button } from 'react-bootstrap';
import { FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styled from '@emotion/styled';

const TextContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    margin: ${(props) => props.margin || '0'};
`;

const TextContent = styled.div`
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: ${(props) => props.color || '#495057'};
    ${(props) =>
        props.isLink &&
        `
        cursor: pointer;
        &:hover {
            color: #50aeb0;
            text-decoration: underline;
        }
    `}
`;

const CopyButton = styled(Button)`
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    min-width: 80px;
    flex-shrink: 0;
`;

function CopyableText({
    text,
    label,
    isLink = false,
    showCopyButton = true,
    copyButtonText = 'Copy',
    containerStyle,
    textStyle,
    buttonStyle,
    fontFamily,
    fontSize,
    color,
    margin,
    onCopy,
    onTextClick,
    ...props
}) {
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(text);
            if (onCopy) {
                onCopy(text);
            } else {
                toast.info('Copied to clipboard!');
            }
        } catch (err) {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy to clipboard');
        }
    };

    const handleTextClick = () => {
        if (isLink && text) {
            window.open(text, '_blank', 'noopener noreferrer');
        }
        if (onTextClick) {
            onTextClick(text);
        }
    };

    return (
        <div {...props}>
            {label && (
                <label
                    css={css`
                        font-weight: bold;
                        margin-bottom: 0.5rem;
                        display: block;
                    `}
                >
                    {label}
                </label>
            )}
            <TextContainer style={containerStyle} margin={margin}>
                <TextContent
                    title={text}
                    onClick={handleTextClick}
                    isLink={isLink}
                    style={textStyle}
                    fontFamily={fontFamily}
                    fontSize={fontSize}
                    color={color}
                >
                    {isLink ? (
                        <a
                            href={text}
                            target="_blank"
                            rel="noopener noreferrer"
                            css={css`
                                color: inherit;
                                text-decoration: none;
                                &:hover {
                                    text-decoration: underline;
                                }
                            `}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {text}
                        </a>
                    ) : (
                        text
                    )}
                </TextContent>

                {showCopyButton && (
                    <CopyButton
                        variant="outline-primary"
                        size="sm"
                        onClick={copyToClipboard}
                        style={buttonStyle}
                        type="button"
                    >
                        <FaCopy />
                        {copyButtonText}
                    </CopyButton>
                )}
            </TextContainer>
        </div>
    );
}

export default CopyableText;
